import {Alert, Button, Separator} from 'platform/components';
import {
  Box,
  Clickable,
  Grid,
  HStack,
  Icon,
  Show,
  StorageImage,
  Text,
  VStack,
  type ThemeIconKey,
} from 'platform/foundation';

import {useState} from 'react';

import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {composePath, EMPTY_PLACEHOLDER, useNavigate} from 'shared/web';

import {useListingParameters} from './useListingParameters';
import {useListingPhotos} from './useListingPhotos';

function ParameterTile(props: {
  icon: ThemeIconKey;
  label: string;
  value: string | null | undefined;
}) {
  return (
    <HStack spacing={3} align="center">
      <Box padding={2} borderRadius="small" backgroundColor="palettes.purple.20.100">
        <Icon value={props.icon} color="palettes.purple.60.100" size={5} />
      </Box>
      <VStack>
        <Text size="xSmall" color="secondary">
          {props.label}
        </Text>
        <Text alternative>{props.value ?? EMPTY_PLACEHOLDER}</Text>
      </VStack>
    </HStack>
  );
}

// The main photo and the thumbnails of the auction listing.
export function ListingGallery(props: {vehicleId: string}) {
  const photos = useListingPhotos(props.vehicleId);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const mainPhoto = photos.find(({id}) => id === selectedPhotoId) ?? photos[0];

  return (
    <VStack spacing={4}>
      <Show when={mainPhoto}>
        {(photo) => (
          <Box borderRadius="small" overflow="hidden">
            <StorageImage url={photo.url} ratio="4 / 3" fit="cover" />
          </Box>
        )}
      </Show>
      <Grid columns={6} spacing={2} data-testid="auction-listing-photos">
        {photos.map((photo) => (
          <Clickable key={photo.id} onClick={() => setSelectedPhotoId(photo.id)}>
            <Box
              borderRadius="small"
              overflow="hidden"
              border="2px solid"
              borderColor={photo.id === mainPhoto?.id ? 'general.accent' : 'general.transparent'}
            >
              <StorageImage url={photo.url} ratio="4 / 3" fit="cover" isLazy />
            </Box>
          </Clickable>
        ))}
      </Grid>
    </VStack>
  );
}

// The photos and the vehicle parameters of the auction listing in the preparation step.
export function ListingVehicle(props: {vehicleId: string}) {
  const photos = useListingPhotos(props.vehicleId);
  const parameters = useListingParameters(props.vehicleId);
  const navigate = useNavigate();
  const photosPath = composePath(vehiclesRoutes.photos, {
    params: {id: props.vehicleId, tab: 'vehicle-photos'},
    isSearchParamsPreserved: false,
  });

  return (
    <VStack spacing={4}>
      <ListingGallery vehicleId={props.vehicleId} />
      <Show when={photos.length === 0}>
        <Alert
          type="inline"
          variant="error"
          message={i18n.t('entity.auction.validation.photos')}
          hyperlinks={[
            {
              title: i18n.t('entity.auction.listing.editPhotos'),
              onClick: () => navigate(photosPath),
            },
          ]}
          data-testid="auction-photos-missing"
        />
      </Show>
      <Show when={photos.length > 0}>
        <HStack>
          <Button
            variant="link"
            leftIcon="image/edit"
            title={i18n.t('entity.auction.listing.editPhotos')}
            to={photosPath}
            data-testid="auction-edit-photos"
          />
        </HStack>
      </Show>
      <Separator spacing={0} />
      <Grid columns={[1, 2, 3]} spacing={4} data-testid="auction-listing-parameters">
        {parameters.map((parameter) => (
          <ParameterTile key={parameter.label} {...parameter} />
        ))}
      </Grid>
    </VStack>
  );
}
